import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { isVoiceEnabled } from '@/services/voice/flag';
import { PATIENT_FILE_RE } from '@/components/layout/SidebarDictateButton';
import { isFullWake, matchSystemCommand, matchWake, type SystemCommandMatch } from '@/services/voice/wake/matcher';
import { blockWake, CELULAR, setWakeState, useWakeBlock, useWakeEngine, useWakeMode } from '@/services/voice/wake/store';
import { useTelaAcesa } from '@/hooks/useTelaAcesa';
import {
  recordWakeArmed,
  recordWakeCommand,
  recordWakeError,
  recordWakeMatch,
  recordWakeOutcome,
  recordWakeRestart,
} from '@/services/voice/wake/wakeLog';
import { startEngineWakeListener, type WakeListenerHandle } from '@/services/voice/wake/engine';
import { pedirComando, useAtlasPorTecla } from '@/services/voice/wake/commands';
import {
  assinarResposta,
  encerrarResposta,
  getRespostaPendente,
  marcarRespostaAberta,
  useRespostaPendente,
  type PedidoResposta,
} from '@/services/voice/wake/resposta';
import {
  atlasCancelar,
  atlasNaoEntendeu,
  atlasOuvindo,
  atlasPedido,
  atlasTerminou,
  rotuloDoPedido,
} from '@/services/voice/wake/atlasState';
import { playVoiceTone } from '@/utils/chime';
import { useOptionalDictation } from './DictationContext';

/** De quanto em quanto tempo o tempo escutando vai pro diário. */
const ARMED_FLUSH_MS = 30_000;
/** Teto de cada parcela: o PC suspenso congela o timer e a volta não pode
 *  somar a noite inteira como "escutando". */
const ARMED_MAX_DELTA_MS = 2 * ARMED_FLUSH_MS;
/** O reconhecedor às vezes entrega "Jarvis" duas vezes seguidas: abre uma só. */
const WAKE_DEBOUNCE_MS = 5000;
/**
 * "Atlas" sozinho: quanto tempo a frase seguinte ainda vale como o pedido.
 * Era 5 s; com a lista de pedidos na tela (05/10/2026) dá tempo de ler antes
 * de falar.
 */
const ATLAS_WAIT_MS = 8000;
/**
 * Tecla do Atlas com a escuta desligada: teto pro reconhecedor ficar pronto
 * (o motor próprio carrega o modelo) antes de desistir com bipe de recusa.
 */
const ATLAS_TECLA_PRONTO_MAX_MS = 15_000;
/** Depois da espera do pedido, a frase que ainda estava sendo fechada chega. */
const ATLAS_TECLA_FOLGA_MS = 1500;

function usePageVisible(): boolean {
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible');
  useEffect(() => {
    const on = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);
  return visible;
}

/**
 * Escuta do "Jarvis" (spec 011). Ouviu (resultado FINAL): abre o ditado já
 * no ao vivo NA HORA (sem janela de cancelar — decisão do Leopoldo, 25/09),
 * que só o médico desliga (X, Alt+V; desde 26/09 sem auto-encerramento); disparo
 * sem querer se desfaz com "Não chamei" na coluna. Liga o ao vivo mesmo sem
 * nenhum clique na página desde que ela carregou.
 *
 * Só escuta quando TUDO vale: modo ligado nesta máquina, papel que dita, ficha
 * de um paciente ou a AGENDA aberta, aba visível e o ditado NÃO está
 * ouvindo — dois reconhecedores no mesmo microfone é
 * combinação não testada, e o ditado tem prioridade. Fora disso, microfone
 * solto. Na agenda o "Jarvis" sozinho é bipe de recusa (não há ficha pra
 * ditar).
 *
 * Comandos do sistema (03/10/2026) têm nome próprio, "Atlas" ("Atlas, abre o
 * próximo / manda entrar / abre a agenda / imprime … / desliga"), e vão pra
 * quem atende na tela (`commands.ts`) — nunca abrem o ditado. "Atlas"
 * sozinho dá o bipe curto e a frase seguinte, sem nome, é o pedido: o Chrome
 * corta a fala na pausa depois do nome (era assim que o "Jarvis, manda
 * entrar" virava ditado).
 *
 * O motor (Chrome ou próprio, fase 5) é preferência desta máquina; trocar
 * religa a escuta no outro. No celular é sempre o próprio (spec 024), e a
 * tela fica acesa enquanto alguém ouve.
 *
 * Tecla do Atlas do controle de apresentação (22/10/2026): vale como dizer
 * "Atlas" agora, com a escuta ligada OU desligada nesta máquina (pedido do
 * Leopoldo: as teclas independem da escuta). Desligada, a tecla liga o
 * reconhecedor só pra ouvir o pedido e desliga de novo depois dele (ou da
 * espera); nesse meio-tempo tudo vale como sempre, "Jarvis" inclusive.
 *
 * Janela de resposta (spec 023, fase 3): a conferência de impressão pede
 * (`resposta.ts`) e a escuta liga do mesmo jeito que pela tecla; pronta, bipe
 * curto e a frase seguinte vai pra quem perguntou ("sim", "pula"…). O que não
 * for resposta segue o caminho de sempre.
 */
export const WakeWordWatcher = () => {
  const ctx = useOptionalDictation();
  const { role } = useAuth();
  const { pathname } = useLocation();
  const modo = useWakeMode();
  const motor = useWakeEngine();
  const bloqueio = useWakeBlock();
  const visible = usePageVisible();

  const openRef = useRef(ctx?.open);
  openRef.current = ctx?.open;
  // Há ficha pra ditar nesta tela? (Na agenda, não.)
  const ditavelRef = useRef(!!ctx?.available);
  ditavelRef.current = !!ctx?.available;

  const ditando = !!ctx?.listening;
  const naFicha = PATIENT_FILE_RE.test(pathname);
  const naAgenda = pathname === '/agenda';
  const ondeEscuta = naFicha || naAgenda;
  const podeOuvir = !bloqueio && !!ctx && isVoiceEnabled(role) && ondeEscuta && visible && !ditando;
  // Tecla do Atlas com a escuta desligada: liga só pro pedido.
  const [porTecla, setPorTecla] = useState(false);
  // Conferência esperando "sim/pula…" (spec 023): liga só pra resposta.
  const esperandoResposta = useRespostaPendente();
  const armed = podeOuvir && (modo === 'ativo' || porTecla || esperandoResposta);
  // Celular: tela apagada corta o microfone — fica acesa enquanto a escuta
  // ou o ditado ouvem (spec 024).
  useTelaAcesa(CELULAR && visible && (armed || ditando));

  // Quem está ouvindo agora (o efeito abaixo) recebe a tecla; sem ninguém, a
  // tecla liga a escuta e o pedido fica guardado até ela ficar pronta.
  const esperarRef = useRef<(() => void) | null>(null);
  const teclaPendenteRef = useRef(false);
  const fimTeclaRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const encerrarTeclaEm = (ms: number) => {
    if (fimTeclaRef.current) clearTimeout(fimTeclaRef.current);
    fimTeclaRef.current = setTimeout(() => {
      fimTeclaRef.current = null;
      teclaPendenteRef.current = false;
      setPorTecla(false);
    }, ms);
  };
  const encerrarTeclaRef = useRef(encerrarTeclaEm);
  encerrarTeclaRef.current = encerrarTeclaEm;
  useEffect(() => () => {
    if (fimTeclaRef.current) clearTimeout(fimTeclaRef.current);
  }, []);
  useAtlasPorTecla(() => {
    if (esperarRef.current) {
      esperarRef.current();
      return;
    }
    teclaPendenteRef.current = true;
    atlasOuvindo(ATLAS_TECLA_PRONTO_MAX_MS);
    setPorTecla(true);
    encerrarTeclaEm(ATLAS_TECLA_PRONTO_MAX_MS);
  }, podeOuvir);

  // Estado pro chip: "pausado" enquanto o ditado ouve, "parado" fora das
  // condições. Travado por rede, o chip fica no erro até religar à mão.
  useEffect(() => {
    if (armed) return;
    if (bloqueio) {
      setWakeState('erro', bloqueio);
      return;
    }
    setWakeState(modo !== 'off' && ondeEscuta && ditando ? 'pausado' : 'parado');
  }, [armed, bloqueio, modo, ondeEscuta, ditando]);

  useEffect(() => {
    if (!armed) return;
    // Só conta o tempo em que o reconhecedor está DE FATO ouvindo: nada de
    // "instalar"/"erro", nem o intervalo entre uma sessão e a religada.
    let desde: number | null = null;
    const flush = () => {
      if (desde === null) return;
      const now = Date.now();
      recordWakeArmed(Math.min(now - desde, ARMED_MAX_DELTA_MS));
      desde = now;
    };
    const tick = setInterval(flush, ARMED_FLUSH_MS);

    let ultimaAbertura = 0;
    // "Atlas" sozinho: até quando a frase seguinte (sem nome) é o pedido.
    let atlasAte = 0;
    // Último pedido ao sistema — o reconhecedor às vezes repete o final.
    let ultimoPedido = { chave: '', at: 0 };
    // "Atlas" sozinho (dito ou pela tecla): a frase seguinte é o pedido.
    const esperarPedido = (agora: number) => {
      if (atlasAte <= agora) playVoiceTone('ouvi');
      atlasAte = agora + ATLAS_WAIT_MS;
      atlasOuvindo(ATLAS_WAIT_MS);
    };
    // Tecla do Atlas: a espera só conta com o reconhecedor de fato ouvindo
    // (o bipe "ouvi" é o "pode falar"); até lá ela fica guardada.
    let pronto = false;
    let teclaEsperando = teclaPendenteRef.current;
    teclaPendenteRef.current = false;
    let handle: WakeListenerHandle | null = null;
    const ouvirTecla = () => {
      if (!pronto) {
        teclaEsperando = true;
        atlasOuvindo(ATLAS_TECLA_PRONTO_MAX_MS);
        return;
      }
      esperarPedido(Date.now());
      handle?.esperarPedido();
      // Escuta ligada só pela tecla: desliga depois da espera.
      encerrarTeclaRef.current(ATLAS_WAIT_MS + ATLAS_TECLA_FOLGA_MS);
    };
    esperarRef.current = ouvirTecla;
    // Janela de resposta da conferência (spec 023): abre com o reconhecedor
    // pronto; fecha com a resposta, no tempo ou se quem perguntou desistiu.
    let resposta: PedidoResposta | null = null;
    let respostaAte = 0;
    let fimResposta: ReturnType<typeof setTimeout> | null = null;
    const fecharResposta = () => {
      if (fimResposta) clearTimeout(fimResposta);
      fimResposta = null;
      resposta = null;
      respostaAte = 0;
    };
    const abrirResposta = () => {
      const p = getRespostaPendente();
      if (p === resposta) return;
      fecharResposta();
      if (!p || !pronto) return;
      resposta = p;
      respostaAte = Date.now() + p.ms;
      marcarRespostaAberta(p);
      playVoiceTone('ouvi');
      // Motor próprio: acorda o ouvido português (ele entende as respostas).
      handle?.esperarPedido();
      fimResposta = setTimeout(() => {
        fecharResposta();
        encerrarResposta(p, 'silencio');
      }, p.ms + ATLAS_TECLA_FOLGA_MS);
    };
    const desassinarResposta = assinarResposta(abrirResposta);
    const pedir = (p: SystemCommandMatch, agora: number, doAtlas = true) => {
      // Escuta ligada só pela tecla: o pedido chegou, pode desligar.
      encerrarTeclaRef.current(0);
      const chave = `${p.comando}:${p.alvos?.join(',') ?? ''}`;
      if (chave === ultimoPedido.chave && agora - ultimoPedido.at < WAKE_DEBOUNCE_MS) return;
      ultimoPedido = { chave, at: agora };
      // Vai pra quem atende na tela (`commands.ts`), que dá o bipe de feito
      // ou de recusa. "Desliga" aqui só acha sessão PAUSADA (ouvindo, a
      // escuta nem liga). Do "Atlas", o brilho mostra o pedido e o resultado.
      const pedido = doAtlas ? atlasPedido(rotuloDoPedido(p.comando, p.alvos)) : 0;
      pedirComando(
        { comando: p.comando, origem: 'escuta', ...(p.alvos ? { alvos: p.alvos } : {}) },
        doAtlas ? { aoTerminar: (ok) => atlasTerminou(pedido, ok) } : undefined,
      );
    };
    handle = startEngineWakeListener(motor, {
      onFinal: (texto) => {
        const agora = Date.now();
        if (resposta && agora <= respostaAte + ATLAS_TECLA_FOLGA_MS) {
          const p = resposta;
          if (p.aoOuvir(texto)) {
            fecharResposta();
            encerrarResposta(p);
            return;
          }
          // Não era resposta ("Jarvis", conversa): segue o caminho de sempre.
        }
        const m = matchWake(texto);
        if (!m) {
          // Frase sem nome logo depois de "Atlas" sozinho: é o pedido.
          if (atlasAte <= agora) return;
          atlasAte = 0;
          const pedido = matchSystemCommand(texto);
          console.info('[wake] atlas → %s', pedido?.comando ?? 'pedido não entendido');
          if (!pedido) {
            playVoiceTone('encerrado');
            atlasNaoEntendeu();
            encerrarTeclaRef.current(0);
            return;
          }
          recordWakeCommand(pedido.comando);
          pedir(pedido, agora);
          return;
        }
        const tipo = recordWakeMatch(m);
        // Só a grafia e o comando — o texto ouvido não sai daqui.
        console.info('[wake] %s (%s%s)', tipo, m.variante, m.comando ? `, ${m.comando}` : '');
        // "Atlas" (03/10/2026): o sistema. Nunca abre o ditado.
        if (m.nome === 'atlas') {
          if (!m.comando) {
            // O Chrome corta "Atlas… manda entrar" na pausa: espera o pedido
            // (o brilho fica aceso enquanto espera).
            esperarPedido(agora);
            return;
          }
          atlasAte = 0;
          pedir({ comando: m.comando, ...(m.alvos ? { alvos: m.alvos } : {}) }, agora);
          return;
        }
        // Chamou o "Jarvis" no meio da espera do "Atlas": apaga o brilho.
        if (atlasAte > agora) atlasCancelar();
        atlasAte = 0;
        if (!isFullWake(m)) return;
        if (agora - ultimaAbertura < WAKE_DEBOUNCE_MS) return;
        // "Jarvis, desliga": o ditado mandando parar (só acha sessão pausada).
        if (m.comando === 'parar') {
          ultimaAbertura = agora;
          pedir({ comando: 'parar' }, agora, false);
          return;
        }
        const open = openRef.current;
        if (!open) return;
        ultimaAbertura = agora;
        if (!ditavelRef.current) {
          // Agenda: não há ficha pra ditar. Recusa audível, não silêncio.
          playVoiceTone('encerrado');
          return;
        }
        recordWakeOutcome('abriu');
        // Sempre já no ao vivo, com ou sem clique na página. Até 27/09 aba
        // sem clique só abria a coluna, e aí os "Jarvis" seguintes caíam no
        // vazio (visto em campo: 6 ouvidos, nenhum ligou). O áudio sem gesto
        // a própria sessão resolve (`liveSession`).
        open({ autoStart: 'live', origem: 'voz' });
      },
      onState: (state, detalhe) => {
        if (state === 'ouvindo') {
          if (desde === null) desde = Date.now();
          if (!pronto) {
            pronto = true;
            if (teclaEsperando) {
              teclaEsperando = false;
              ouvirTecla();
            }
            abrirResposta();
          }
        } else {
          flush();
          desde = null;
        }
        // A tecla ligou a escuta e ela não sobe: recusa audível, e desliga.
        if (teclaEsperando && (state === 'instalar' || state === 'sem-suporte' || state === 'erro')) {
          teclaEsperando = false;
          atlasCancelar();
          playVoiceTone('encerrado');
          encerrarTeclaRef.current(0);
        }
        if (state === 'erro' && detalhe) recordWakeError(detalhe);
        // Modo local foi à rede: trava até religar à mão (sobrevive à recarga).
        if (state === 'erro' && detalhe === 'network') {
          blockWake(detalhe);
          return;
        }
        setWakeState(state, detalhe ?? null);
      },
      onRestart: () => {
        flush();
        desde = null;
        recordWakeRestart();
      },
    });
    return () => {
      esperarRef.current = null;
      desassinarResposta();
      // A escuta caiu com a janela aberta (o sistema voltou a falar, saiu da
      // ficha): quem perguntou pede de novo quando precisar.
      if (resposta) {
        const p = resposta;
        fecharResposta();
        encerrarResposta(p, 'cancelada');
      }
      handle?.();
      clearInterval(tick);
      flush();
      // A escuta parou (ditado ligou, saiu da ficha): nada mais vai chegar.
      atlasCancelar();
    };
  }, [armed, motor]);

  return null;
};
